import { useStocksStore } from "~/stores/useStocksStore";

/**
 * Defers the heavy /api/symbols-list fetch (~1.7 MiB) until the browser is
 * idle, instead of firing it inside onMounted and competing with LCP/TTI on
 * first paint.
 *
 * - On SSR: no-op (the store init reads localforage which only exists client-side).
 * - On the client: schedules `stocksStore.init()` behind requestIdleCallback,
 *   falling back to a short setTimeout. Skips if the list is already loaded
 *   (the store already caches in IndexedDB for 1h, so warm sessions are
 *   instant).
 * - Returns an `eagerInit()` function that components can call on user
 *   interaction (focus/click on a search input, etc.) to upgrade the deferred
 *   load to an immediate one — useful when the user is about to consume the
 *   data anyway.
 */
export function useDeferredSymbolsInit(options?: {
	/** Max ms to wait for an idle slot before forcing the fetch. */
	timeoutMs?: number;
	/** Fallback delay when requestIdleCallback isn't available. */
	fallbackDelayMs?: number;
}) {
	const stocksStore = useStocksStore();
	const timeoutMs = options?.timeoutMs ?? 4000;
	const fallbackDelayMs = options?.fallbackDelayMs ?? 2000;

	let scheduledHandle: number | null = null;
	let scheduledTimer: ReturnType<typeof setTimeout> | null = null;
	let initFired = false;

	const cancelScheduled = () => {
		if (typeof window === "undefined") return;
		const ric = (window as unknown as { cancelIdleCallback?: (h: number) => void })
			.cancelIdleCallback;
		if (scheduledHandle !== null && typeof ric === "function") {
			ric(scheduledHandle);
		}
		scheduledHandle = null;
		if (scheduledTimer !== null) {
			clearTimeout(scheduledTimer);
			scheduledTimer = null;
		}
	};

	const fireInit = () => {
		if (initFired) return;
		initFired = true;
		cancelScheduled();
		if (stocksStore.symbols_list.length === 0) {
			stocksStore.init();
		}
	};

	const scheduleIdle = () => {
		if (typeof window === "undefined") return;
		if (initFired) return;
		if (stocksStore.symbols_list.length > 0) return;

		const ric = (
			window as unknown as {
				requestIdleCallback?: (
					cb: () => void,
					opts?: { timeout: number }
				) => number;
			}
		).requestIdleCallback;

		if (typeof ric === "function") {
			scheduledHandle = ric(fireInit, { timeout: timeoutMs });
		} else {
			scheduledTimer = setTimeout(fireInit, fallbackDelayMs);
		}
	};

	if (import.meta.client) {
		scheduleIdle();
	}

	return {
		/**
		 * Call from a focus/click/keydown handler on the search input to
		 * upgrade the deferred load to an immediate one. Safe to call
		 * repeatedly — only fetches once.
		 */
		eagerInit: fireInit
	};
}
